import Popover from '@components/Common/Popover.js';
import { BuildingIcon, ChevronIcon, UserIcon } from '@icons/index.js';
import OrganizationState from '@states/Organization';
import { UIDiv, UIText } from '@ui';
import i18next from 'i18next';

// Constants for project types
const PROJECT_TYPES = {
  PERSONAL: 'personal',
  TEAM: 'team',
};

// Configuration for project type options
const PROJECT_TYPE_CONFIG = {
  [PROJECT_TYPES.PERSONAL]: {
    icon: UserIcon,
    labelKey: 'common.my_projects',
  },
  [PROJECT_TYPES.TEAM]: {
    icon: BuildingIcon,
    labelKey: 'common.team_projects',
  },
};

export default class ProjectTypePicker extends UIDiv {
  constructor({
    projectType = PROJECT_TYPES.PERSONAL,
    handleProjectTypeChange = () => Promise.resolve(),
    minifiedView = false,
  }) {
    super();
    this.currentProjectType = projectType;
    this.handleProjectTypeChange = handleProjectTypeChange;
    this.minifiedView = minifiedView;

    this.render();

    // Re-render when organizations load so the disabled state updates
    this._unsubscribeOrg = OrganizationState.subscribe(() => {
      this.render();
    });
  }

  render() {
    this.clear();
    const dropdownTrigger = this.createDropdownTrigger();
    const dropdownContent = this.createDropdownContent();

    dropdownTrigger.onClick(e => {
      if (OrganizationState.organizations.length === 0) return;
      e.stopPropagation();
      Popover.show({
        trigger: dropdownTrigger,
        content: dropdownContent,
        placement: 'bottom-start',
        hideOnSelect: true,
        className: 'w-[160px]',
        onShow: () => this.handleChevronRotation(true),
        onHide: () => this.handleChevronRotation(false),
      });
    });
    this.add(dropdownTrigger);
  }

  createDropdownTrigger() {
    const isDisabled = OrganizationState.organizations.length === 0;
    const triggerWidth = this.minifiedView ? 'w-10' : 'w-[135px] md:w-[160px]';
    const dropdownTrigger = new UIDiv().setClass(
      `${triggerWidth} bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 cursor-pointer hover:bg-gray-50 flex items-center justify-between min-h-8${isDisabled ? ' opacity-50 cursor-not-allowed pointer-events-none' : ''}`,
    );

    // Add project type icon
    const config = PROJECT_TYPE_CONFIG[this.currentProjectType];

    // const IconComponent = config.icon;
    // const projectIcon = new IconComponent({
    //   size: 16,
    //   color: 'none',
    //   strokeColor: 'currentColor',
    // });

    if (this.minifiedView) {
      // In minified view, only show the project type icon (centered)
      dropdownTrigger.setClass(
        `${triggerWidth} bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 cursor-pointer hover:bg-gray-50 flex items-center justify-center min-h-8${isDisabled ? ' opacity-50 cursor-not-allowed pointer-events-none' : ''}`,
      );
      //   dropdownTrigger.add(projectIcon);
    } else {
      // In normal view, show text and chevron
      const selectedText = new UIText(this.getSelectedText()).setClass(
        'flex-1 text-left',
      );

      this.chevronIcon = new UIDiv().setClass(
        'w-5 h-5 text-gray-400 transform rotate-90 transition-transform duration-200',
      );
      const chevronIconComponent = new ChevronIcon({ size: 16, color: 'none' });
      this.chevronIcon.add(chevronIconComponent);

      dropdownTrigger.add(selectedText, this.chevronIcon);
    }

    return dropdownTrigger;
  }

  createDropdownContent() {
    const dropdownContent = new UIDiv().setClass(
      'border border-gray-200 rounded-lg max-h-128 overflow-y-auto',
    );

    // Add options for all project types
    Object.keys(PROJECT_TYPE_CONFIG).forEach(type => {
      const option = this.createProjectTypeOption(type);
      dropdownContent.add(option);
    });

    return dropdownContent;
  }

  createProjectTypeOption(type) {
    const config = PROJECT_TYPE_CONFIG[type];
    const isSelected = this.currentProjectType === type;

    const option = new UIDiv().setClass(
      'px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center text-gray-900 gap-2',
    );

    // Create icon
    const IconComponent = config.icon;
    const icon = new IconComponent({
      size: 20,
      strokeColor: isSelected ? 'var(--o-hive-primary-80)' : '',
      color: 'none',
    });
    option.add(icon);

    // Create text with conditional styling
    const textClass = `${'flex-1 text-left'} ${isSelected ? 'text-[var(--o-hive-primary-80)] font-semibold' : ''}`;
    const text = new UIText(i18next.t(config.labelKey)).setClass(textClass);
    option.add(text);

    // Add click handler
    option.onClick(() => this.handleOptionClick(type));

    return option;
  }

  async handleOptionClick(type) {
    if (this.currentProjectType === type) return; // No change if already selected
    const result = await this.handleProjectTypeChange(type);
    if (result === 'error') return; // Handle error case if needed
    this.currentProjectType = type;
    this.render();
  }

  handleChevronRotation(isOpen) {
    // Only handle chevron rotation if not in minified view and chevron exists
    if (!this.minifiedView && this.chevronIcon) {
      const rotationClass = isOpen
        ? 'w-5 h-5 text-gray-400 transform -rotate-90 transition-transform duration-200'
        : 'w-5 h-5 text-gray-400 transform rotate-90 transition-transform duration-200';
      this.chevronIcon.setClass(rotationClass);
    }
  }

  getSelectedText() {
    const config = PROJECT_TYPE_CONFIG[this.currentProjectType];
    return config ? i18next.t(config.labelKey) : '';
  }

  dispose() {
    this._unsubscribeOrg?.();
  }
}
